import {
  Callout,
  Card,
  ControlGroup,
  FormGroup,
  H6,
  HTMLSelect,
  InputGroup,
  NumericInput,
  SegmentedControl,
  Switch,
} from "@blueprintjs/core";
import {
  getComponentHelperText,
  getComponentIntent,
  MediaImage,
  POST_QR_CODE_DEFAULT_BACKDROP_COLOR,
  POST_QR_CODE_DEFAULT_BORDER_COLOR,
  POST_QR_CODE_DEFAULT_BOX_COLOR,
  POST_QR_CODE_DEFAULT_QR_BACKGROUND_COLOR,
  POST_QR_CODE_DEFAULT_QR_COLOR,
  POST_QR_CODE_DEFAULT_TEXT,
  POST_QR_CODE_DEFAULT_TEXT_COLOR,
  POST_QR_CODE_DEFAULT_TEXT_SIZE,
  PostQrCode,
  PostQrCodeBox,
} from "@package/ui-apollo-components";
import {
  type CanvasRect,
  ColorInput,
  LayoutCanvasEditor,
  LayoutCanvasPositionInputs,
} from "@package/ui-components";
import {
  type Post,
  PostQrCodeAlignHorizontal,
  PostQrCodeAlignVertical,
  PostQrCodeBoxShape,
  PostQrCodeLocationType,
  type PostQrCodeStyle,
  type ServiceOptionAppCmsFunction,
  useGetServiceQuery,
} from "@package/ui-gql/index-data-access";
import type { ValidationError } from "class-validator";
import { type WritableDraft } from "immer";
import { get } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

type PostQrCodeCanvasProps = {
  disabled: boolean;
  post: Post;
  errors: ValidationError[];
  updatePost: (callback: (draft: WritableDraft<Post>) => void) => void;
};

// The visual editor's placement is no longer a 1:1 mapping onto
// PostQrCodeLocationType — "percent" covers any rect stored in the new
// qrCodeLocationPercent field, regardless of what qrCodeLocation.type it
// happens to mirror for old-bundle compatibility (see applyPercentRect).
type PlacementOption = "top" | "bottom" | "percent";

const PLACEMENT_OPTIONS: { value: PlacementOption; label: string }[] = [
  { value: "bottom", label: "Bottom" },
  { value: "top", label: "Top" },
  { value: "percent", label: "Custom Position" },
];

const BOX_SHAPE_OPTIONS: { value: PostQrCodeBoxShape; label: string }[] = [
  { value: PostQrCodeBoxShape.Square, label: "Square" },
  { value: PostQrCodeBoxShape.Rounded, label: "Rounded" },
  { value: PostQrCodeBoxShape.Pill, label: "Pill" },
];

const QR_ALIGN_HORIZONTAL_OPTIONS: {
  value: PostQrCodeAlignHorizontal;
  label: string;
}[] = [
  { value: PostQrCodeAlignHorizontal.Left, label: "Left" },
  { value: PostQrCodeAlignHorizontal.Center, label: "Center" },
  { value: PostQrCodeAlignHorizontal.Right, label: "Right" },
];

const QR_ALIGN_VERTICAL_OPTIONS: {
  value: PostQrCodeAlignVertical;
  label: string;
}[] = [
  { value: PostQrCodeAlignVertical.Top, label: "Top" },
  { value: PostQrCodeAlignVertical.Center, label: "Middle" },
  { value: PostQrCodeAlignVertical.Bottom, label: "Bottom" },
];

const ASPECT_PRESETS = {
  landscape: { width: 1920, height: 1080, label: "Landscape (16:9)" },
  portrait: { width: 1080, height: 1920, label: "Portrait (9:16)" },
  square: { width: 1080, height: 1080, label: "Square (1:1)" },
};

// LED canvases are frequently an odd size that doesn't match any standard
// ratio — this is a real, selectable option, not a fallback.
const ASPECT_CUSTOM_KEY = "custom" as const;
const ASPECT_CUSTOM_LABEL = "Custom Size";
const ASPECT_CUSTOM_DEFAULT_SIZE = { width: 1920, height: 1080 };

type AspectKey = keyof typeof ASPECT_PRESETS | typeof ASPECT_CUSTOM_KEY;

type PercentRect = { x: number; y: number; width: number; height: number };

// Roughly the footprint of the Bottom preset at 16:9 — used only to seed a
// visible starting box the first time a post switches to Custom Position and
// there's no previously-authored rect to restore (see savedPercentRect).
const DEFAULT_CANVAS_PERCENT_RECT: PercentRect = {
  x: 35,
  y: 54,
  width: 30,
  height: 38,
};

function toCanvasRect(
  percent: PercentRect,
  size: { width: number; height: number },
): CanvasRect {
  return {
    x: (percent.x / 100) * size.width,
    y: (percent.y / 100) * size.height,
    width: (percent.width / 100) * size.width,
    height: (percent.height / 100) * size.height,
  };
}

function fromCanvasRect(
  rect: CanvasRect,
  size: { width: number; height: number },
): PercentRect {
  return {
    x: Math.round((rect.x / size.width) * 1000) / 10,
    y: Math.round((rect.y / size.height) * 1000) / 10,
    width: Math.round((rect.width / size.width) * 1000) / 10,
    height: Math.round((rect.height / size.height) * 1000) / 10,
  };
}

// Applies the percentage rect from the visual editor, then dual-writes the
// pixel mirror onto qrCodeLocation so a pre-percent wall bundle degrades
// gracefully instead of rendering a stale ~30px box in the corner.
function applyPercentRect(
  draft: WritableDraft<Post>,
  pct: PercentRect,
  flyoutPosition: Post["flyoutPosition"],
) {
  draft.qrCodeLocationPercent = pct;

  if (flyoutPosition?.width && flyoutPosition?.height) {
    // Exact pixel mirror — flyoutPosition IS the wall-side flyout box.
    draft.qrCodeLocation = {
      ...draft.qrCodeLocation,
      type: PostQrCodeLocationType.Custom,
      x: (pct.x / 100) * flyoutPosition.width,
      y: (pct.y / 100) * flyoutPosition.height,
      width: (pct.width / 100) * flyoutPosition.width,
      height: (pct.height / 100) * flyoutPosition.height,
    };
  } else {
    // Fullscreen flyout: size varies per device, so no honest pixel value
    // exists. Degrade to whichever preset the rect actually sits nearest,
    // and null out the pixel fields — a stale rect here would otherwise be
    // read by an old bundle that ignores `type`.
    const nearest =
      pct.y + pct.height / 2 < 50
        ? PostQrCodeLocationType.Top
        : PostQrCodeLocationType.Bottom;
    draft.qrCodeLocation = {
      ...draft.qrCodeLocation,
      type: nearest,
      x: null,
      y: null,
      width: null,
      height: null,
    };
  }
}

// Mirrors the Top/Bottom CSS from FlyoutQrCode.tsx so the preview matches
// the wall exactly for these two presets, which never carry x/y/width/height.
// Content is drawn at literal wall pixels (canvasSize) and the whole
// assembly is scaled down to fit the measured preview container, so the QR
// box's own padding/QR/caption sizing stays pixel-exact with the wall.
function PostQrCodePresetPreview({
  type,
  post,
  canvasSize,
  location,
  profile,
}: {
  type: "top" | "bottom";
  post: Post;
  canvasSize: { width: number; height: number };
  location: string;
  profile: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(
    function effectOnPresetPreviewContainerResize() {
      const el = containerRef.current;
      if (!el || !canvasSize.width) {
        return;
      }

      const observer = new ResizeObserver(() => {
        setScale(el.clientWidth / canvasSize.width);
      });
      observer.observe(el);
      setScale(el.clientWidth / canvasSize.width);

      return () => observer.disconnect();
    },
    [canvasSize.width],
  );

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden">
      <div
        style={{
          width: canvasSize.width,
          height: canvasSize.height,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        <div
          className={
            type === "top"
              ? "absolute top-1/3 left-1/2"
              : "absolute bottom-1/3 left-1/2"
          }
          style={{
            transform:
              type === "top"
                ? "translate(-50%, -33.3%)"
                : "translate(-50%, 33.3%)",
          }}
        >
          <PostQrCodeBox text={post.qrCodeText} style={post.qrCodeStyle}>
            <PostQrCode
              location={location}
              profile={profile}
              post={post}
              fgColor={post.qrCodeStyle?.qrColor ?? undefined}
              bgColor={post.qrCodeStyle?.qrBackgroundColor ?? undefined}
            />
          </PostQrCodeBox>
        </div>
      </div>
    </div>
  );
}

export function PostQrCodeCanvas({
  disabled,
  post,
  errors,
  updatePost,
}: PostQrCodeCanvasProps) {
  const [aspectKey, setAspectKey] = useState<AspectKey>("landscape");
  const [customSize, setCustomSize] = useState(ASPECT_CUSTOM_DEFAULT_SIZE);
  // Remembers the last authored percent rect across a percent -> Top/Bottom
  // -> percent round trip, since leaving percent placement now clears
  // qrCodeLocationPercent entirely (see handlePlacementChange).
  const [savedPercentRect, setSavedPercentRect] = useState<PercentRect | null>(
    null,
  );

  const { data: dataService } = useGetServiceQuery({
    variables: { id: post.flyoutType ?? "" },
    skip: !post.flyoutType,
  });

  const cmsPostOptions = dataService?.item?.options?.cms?.post as
    | ServiceOptionAppCmsFunction
    | undefined;

  const previewMediaId = useMemo<string | undefined>(
    () =>
      cmsPostOptions?.previewMediaSource
        ? (get(post, cmsPostOptions.previewMediaSource) ?? undefined)
        : undefined,
    [cmsPostOptions, post],
  );

  const representativeLink = post.qrCodeLinkByLocationProfile?.find(
    (q) => q?.qrCodeLink,
  );
  const previewLocation = representativeLink?.location ?? "";
  const previewProfile = representativeLink?.profile ?? "";

  const canvasSize = useMemo(() => {
    if (post.flyoutPosition?.width && post.flyoutPosition?.height) {
      return {
        width: post.flyoutPosition.width,
        height: post.flyoutPosition.height,
      };
    }

    if (aspectKey === ASPECT_CUSTOM_KEY) {
      // Guarantee a valid, visible preview even mid-edit of the inputs
      // below rather than letting a 0/blank value collapse the canvas.
      return {
        width: Math.max(1, customSize.width || 1),
        height: Math.max(1, customSize.height || 1),
      };
    }

    return ASPECT_PRESETS[aspectKey];
  }, [aspectKey, customSize, post.flyoutPosition]);

  if (!post.flyoutType) {
    return (
      <Callout intent="primary" title="Choose an Interactive Type first">
        Select an Interactive Type on the Interactive tab to position and style
        the QR code.
      </Callout>
    );
  }

  if (cmsPostOptions?.qrCodeEmbeddedInFlyout) {
    return (
      <Callout title="Positioned by the Interactive Type">
        This Interactive Type renders the QR code inside its own layout. Its
        position and appearance are controlled there, not here.
      </Callout>
    );
  }

  const hasAnyLink =
    !!post.qrCodeLink ||
    !!post.qrCodeLinkByLocationProfile?.some((q) => q?.qrCodeLink);

  if (!hasAnyLink) {
    return (
      <Callout intent="primary" title="Add a QR Code Link first">
        Enter a QR Code Link above to preview and position it.
      </Callout>
    );
  }

  const hasPercentRect =
    !!post.qrCodeLocationPercent?.width && !!post.qrCodeLocationPercent?.height;
  const isLegacyCustom =
    !hasPercentRect &&
    post.qrCodeLocation?.type === PostQrCodeLocationType.Custom;

  const placement: PlacementOption | "legacy" = hasPercentRect
    ? "percent"
    : isLegacyCustom
      ? "legacy"
      : post.qrCodeLocation?.type === PostQrCodeLocationType.Top
        ? "top"
        : "bottom";

  const isPercentPlacement = placement === "percent";

  const canvasPercentRect: PercentRect = {
    x: post.qrCodeLocationPercent?.x ?? DEFAULT_CANVAS_PERCENT_RECT.x,
    y: post.qrCodeLocationPercent?.y ?? DEFAULT_CANVAS_PERCENT_RECT.y,
    width:
      post.qrCodeLocationPercent?.width ?? DEFAULT_CANVAS_PERCENT_RECT.width,
    height:
      post.qrCodeLocationPercent?.height ?? DEFAULT_CANVAS_PERCENT_RECT.height,
  };

  const canvasRect = toCanvasRect(canvasPercentRect, canvasSize);

  const handlePlacementChange = (value: string) => {
    const nextPlacement = value as PlacementOption;

    if (nextPlacement === "percent") {
      const rect = savedPercentRect ?? DEFAULT_CANVAS_PERCENT_RECT;
      setSavedPercentRect(rect);
      updatePost((draft) => {
        applyPercentRect(draft, rect, post.flyoutPosition);
      });
      return;
    }

    // Leaving percent placement — remember the authored rect so switching
    // back restores it instead of re-seeding the default.
    if (hasPercentRect && post.qrCodeLocationPercent) {
      setSavedPercentRect({
        x: post.qrCodeLocationPercent.x ?? 0,
        y: post.qrCodeLocationPercent.y ?? 0,
        width: post.qrCodeLocationPercent.width ?? 0,
        height: post.qrCodeLocationPercent.height ?? 0,
      });
    }

    updatePost((draft) => {
      draft.qrCodeLocationPercent = null;
      draft.qrCodeLocation = {
        ...draft.qrCodeLocation,
        type:
          nextPlacement === "top"
            ? PostQrCodeLocationType.Top
            : PostQrCodeLocationType.Bottom,
        x: null,
        y: null,
        width: null,
        height: null,
      };
    });
  };

  const handleCanvasRectChange = (rect: CanvasRect) => {
    const pct = fromCanvasRect(rect, canvasSize);
    setSavedPercentRect(pct);
    updatePost((draft) => {
      applyPercentRect(draft, pct, post.flyoutPosition);
    });
  };

  const updateStyle = (patch: Partial<PostQrCodeStyle>) => {
    updatePost((draft) => {
      draft.qrCodeStyle = { ...draft.qrCodeStyle, ...patch };
    });
  };

  const minTileSize = canvasSize.width * 0.05;

  return (
    <div className="flex flex-col gap-4">
      <Card compact>
        <H6>Placement</H6>
        <div className="flex flex-col gap-4">
          <FormGroup label="Position" disabled={disabled}>
            <SegmentedControl
              fill
              disabled={disabled}
              options={PLACEMENT_OPTIONS}
              value={placement}
              onValueChange={handlePlacementChange}
            />
          </FormGroup>
          {!post.flyoutPosition?.width && (
            <FormGroup
              label="Preview Aspect Ratio"
              disabled={disabled}
              helperText="Position is stored as a percentage of the interactive area, so it adapts to any wall. This only changes how the preview looks — pick Custom Size to match an odd-shaped LED canvas."
            >
              <ControlGroup>
                <HTMLSelect
                  disabled={disabled}
                  value={aspectKey}
                  onChange={(e) =>
                    setAspectKey(e.currentTarget.value as AspectKey)
                  }
                >
                  {Object.entries(ASPECT_PRESETS).map(([key, preset]) => (
                    <option key={key} value={key}>
                      {preset.label}
                    </option>
                  ))}
                  <option value={ASPECT_CUSTOM_KEY}>
                    {ASPECT_CUSTOM_LABEL}
                  </option>
                </HTMLSelect>
                {aspectKey === ASPECT_CUSTOM_KEY && (
                  <>
                    <NumericInput
                      disabled={disabled}
                      min={1}
                      allowNumericCharactersOnly
                      placeholder="Width"
                      value={customSize.width}
                      onValueChange={(value) =>
                        setCustomSize((prev) => ({ ...prev, width: value }))
                      }
                    />
                    <NumericInput
                      disabled={disabled}
                      min={1}
                      allowNumericCharactersOnly
                      placeholder="Height"
                      value={customSize.height}
                      onValueChange={(value) =>
                        setCustomSize((prev) => ({ ...prev, height: value }))
                      }
                    />
                  </>
                )}
              </ControlGroup>
            </FormGroup>
          )}
        </div>
      </Card>
      <div>
        <H6>Preview</H6>
        {placement === "percent" ? (
          <>
            <LayoutCanvasEditor
              width={canvasSize.width}
              height={canvasSize.height}
              positions={[canvasRect]}
              disabled={disabled}
              selectedIndex={0}
              minTileSize={minTileSize}
              gridSize={minTileSize}
              snapEnabled
              background={
                previewMediaId ? (
                  <MediaImage id={previewMediaId} fill />
                ) : undefined
              }
              renderTile={(_index, rect, scale) => (
                <div
                  style={{
                    width: rect.width,
                    height: rect.height,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                  }}
                >
                  <PostQrCodeBox
                    text={post.qrCodeText}
                    style={post.qrCodeStyle}
                    fill
                  >
                    <PostQrCode
                      location={previewLocation}
                      profile={previewProfile}
                      post={post}
                      fgColor={post.qrCodeStyle?.qrColor ?? undefined}
                      bgColor={post.qrCodeStyle?.qrBackgroundColor ?? undefined}
                    />
                  </PostQrCodeBox>
                </div>
              )}
              showIndexBadges={false}
              onSelect={() => {}}
              onChange={(_, rect) => handleCanvasRectChange(rect)}
            />
            <div className="mt-3">
              <LayoutCanvasPositionInputs
                index={0}
                rect={canvasRect}
                canvasWidth={canvasSize.width}
                canvasHeight={canvasSize.height}
                minTileSize={minTileSize}
                disabled={disabled}
                label="QR Code Box"
                onChange={handleCanvasRectChange}
                onDelete={() => handlePlacementChange("bottom")}
                deleteIcon="undo"
                deleteIntent="none"
                deleteTitle="Switch back to Bottom placement"
              />
            </div>
          </>
        ) : placement === "legacy" ? (
          <Callout intent="warning" title="Legacy custom position">
            This content uses a legacy pixel position from before the visual
            editor existed. Switch to Custom Position above to edit it visually
            — this replaces it with a percentage-based position, so the QR code
            stays in place on any wall.
          </Callout>
        ) : (
          <div
            className="relative bg-slate-100 border border-slate-300 rounded overflow-hidden"
            style={{
              width: "100%",
              aspectRatio: `${canvasSize.width} / ${canvasSize.height}`,
            }}
          >
            {previewMediaId && (
              <div className="absolute inset-0 overflow-hidden">
                <MediaImage id={previewMediaId} fill />
              </div>
            )}
            <PostQrCodePresetPreview
              type={placement}
              post={post}
              canvasSize={canvasSize}
              location={previewLocation}
              profile={previewProfile}
            />
          </div>
        )}
        {!previewMediaId && !!cmsPostOptions?.previewMediaSource && (
          <Callout className="mt-3">
            Add Media on the Interactive tab to preview it behind the QR code.
          </Callout>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-w-0">
        <Card compact className="min-w-0">
          <H6>Box</H6>
          <div className="flex flex-col gap-3 min-w-0">
            <FormGroup label="Backdrop" disabled={disabled}>
              <ColorInput
                id="qr-code-backdrop-color"
                alpha
                disabled={disabled}
                value={
                  post.qrCodeStyle?.backdropColor ??
                  POST_QR_CODE_DEFAULT_BACKDROP_COLOR
                }
                onValueChange={(v) => updateStyle({ backdropColor: v })}
              />
            </FormGroup>
            <FormGroup label="Color" disabled={disabled}>
              <ColorInput
                disabled={disabled}
                value={
                  post.qrCodeStyle?.boxColor ?? POST_QR_CODE_DEFAULT_BOX_COLOR
                }
                onValueChange={(v) => updateStyle({ boxColor: v })}
              />
            </FormGroup>
            <FormGroup label="Shape" disabled={disabled}>
              <SegmentedControl
                fill
                disabled={disabled}
                options={BOX_SHAPE_OPTIONS}
                value={post.qrCodeStyle?.boxShape ?? PostQrCodeBoxShape.Rounded}
                onValueChange={(v) =>
                  updateStyle({ boxShape: v as PostQrCodeBoxShape })
                }
              />
            </FormGroup>
            <FormGroup disabled={disabled}>
              <Switch
                disabled={disabled}
                label="Border"
                checked={!!post.qrCodeStyle?.borderEnabled}
                onChange={() =>
                  updateStyle({
                    borderEnabled: !post.qrCodeStyle?.borderEnabled,
                  })
                }
              />
            </FormGroup>
            <FormGroup
              label="Border Color"
              disabled={disabled || !post.qrCodeStyle?.borderEnabled}
            >
              <ColorInput
                disabled={disabled || !post.qrCodeStyle?.borderEnabled}
                value={
                  post.qrCodeStyle?.borderColor ??
                  POST_QR_CODE_DEFAULT_BORDER_COLOR
                }
                onValueChange={(v) => updateStyle({ borderColor: v })}
              />
            </FormGroup>
          </div>
        </Card>
        <Card compact className="min-w-0">
          <H6>QR Code</H6>
          <div className="flex flex-col gap-3 min-w-0">
            {isPercentPlacement && (
              <>
                <FormGroup label="Horizontal Position" disabled={disabled}>
                  <SegmentedControl
                    fill
                    disabled={disabled}
                    options={QR_ALIGN_HORIZONTAL_OPTIONS}
                    value={
                      post.qrCodeStyle?.qrAlignHorizontal ??
                      PostQrCodeAlignHorizontal.Center
                    }
                    onValueChange={(v) =>
                      updateStyle({
                        qrAlignHorizontal: v as PostQrCodeAlignHorizontal,
                      })
                    }
                  />
                </FormGroup>
                <FormGroup label="Vertical Position" disabled={disabled}>
                  <SegmentedControl
                    fill
                    disabled={disabled}
                    options={QR_ALIGN_VERTICAL_OPTIONS}
                    value={
                      post.qrCodeStyle?.qrAlignVertical ??
                      PostQrCodeAlignVertical.Center
                    }
                    onValueChange={(v) =>
                      updateStyle({
                        qrAlignVertical: v as PostQrCodeAlignVertical,
                      })
                    }
                  />
                </FormGroup>
              </>
            )}
            <FormGroup label="Color" disabled={disabled}>
              <ColorInput
                disabled={disabled}
                value={
                  post.qrCodeStyle?.qrColor ?? POST_QR_CODE_DEFAULT_QR_COLOR
                }
                onValueChange={(v) => updateStyle({ qrColor: v })}
              />
            </FormGroup>
            <FormGroup label="Background" disabled={disabled}>
              <ColorInput
                disabled={disabled}
                value={
                  post.qrCodeStyle?.qrBackgroundColor ??
                  POST_QR_CODE_DEFAULT_QR_BACKGROUND_COLOR
                }
                onValueChange={(v) => updateStyle({ qrBackgroundColor: v })}
              />
            </FormGroup>
            <FormGroup
              label="Size"
              disabled={disabled}
              helperText={getComponentHelperText<PostQrCodeStyle>(
                "qrSize",
                errors,
                "In pixels. Leave blank to use the default size.",
              )}
              intent={getComponentIntent<PostQrCodeStyle>("qrSize", errors)}
            >
              <NumericInput
                fill
                disabled={disabled}
                min={32}
                allowNumericCharactersOnly
                value={post.qrCodeStyle?.qrSize ?? undefined}
                onValueChange={(value, valueAsString) =>
                  updateStyle({
                    qrSize: valueAsString.length === 0 ? null : value,
                  })
                }
              />
            </FormGroup>
          </div>
        </Card>
        <Card compact className="min-w-0">
          <H6>Caption</H6>
          <div className="flex flex-col gap-3 min-w-0">
            <FormGroup
              label="Text"
              disabled={disabled}
              helperText={getComponentHelperText<Post>(
                "qrCodeText",
                errors,
                `Overrides the default text of '${POST_QR_CODE_DEFAULT_TEXT}'`,
              )}
              intent={getComponentIntent<Post>("qrCodeText", errors)}
            >
              <InputGroup
                disabled={disabled || !!post.qrCodeStyle?.textHidden}
                placeholder="QR Code Text"
                value={post.qrCodeText ?? ""}
                onValueChange={(value) =>
                  updatePost((draft) => {
                    draft.qrCodeText = value;
                  })
                }
              />
            </FormGroup>
            <FormGroup
              label="Color"
              disabled={disabled || !!post.qrCodeStyle?.textHidden}
            >
              <ColorInput
                disabled={disabled || !!post.qrCodeStyle?.textHidden}
                value={
                  post.qrCodeStyle?.textColor ?? POST_QR_CODE_DEFAULT_TEXT_COLOR
                }
                onValueChange={(v) => updateStyle({ textColor: v })}
              />
            </FormGroup>
            <FormGroup
              label="Size"
              disabled={disabled || !!post.qrCodeStyle?.textHidden}
              helperText={getComponentHelperText<PostQrCodeStyle>(
                "textSize",
                errors,
                "In pixels.",
              )}
              intent={getComponentIntent<PostQrCodeStyle>("textSize", errors)}
            >
              <NumericInput
                fill
                disabled={disabled || !!post.qrCodeStyle?.textHidden}
                min={8}
                allowNumericCharactersOnly
                value={
                  post.qrCodeStyle?.textSize ?? POST_QR_CODE_DEFAULT_TEXT_SIZE
                }
                onValueChange={(value) => updateStyle({ textSize: value })}
              />
            </FormGroup>
            {isPercentPlacement && (
              <>
                <FormGroup
                  label="Horizontal Position"
                  disabled={disabled || !!post.qrCodeStyle?.textHidden}
                >
                  <SegmentedControl
                    fill
                    disabled={disabled || !!post.qrCodeStyle?.textHidden}
                    options={QR_ALIGN_HORIZONTAL_OPTIONS}
                    value={
                      post.qrCodeStyle?.textAlignHorizontal ??
                      PostQrCodeAlignHorizontal.Center
                    }
                    onValueChange={(v) =>
                      updateStyle({
                        textAlignHorizontal: v as PostQrCodeAlignHorizontal,
                      })
                    }
                  />
                </FormGroup>
                <FormGroup
                  label="Vertical Position"
                  disabled={disabled || !!post.qrCodeStyle?.textHidden}
                >
                  <SegmentedControl
                    fill
                    disabled={disabled || !!post.qrCodeStyle?.textHidden}
                    options={QR_ALIGN_VERTICAL_OPTIONS}
                    value={
                      post.qrCodeStyle?.textAlignVertical ??
                      PostQrCodeAlignVertical.Bottom
                    }
                    onValueChange={(v) =>
                      updateStyle({
                        textAlignVertical: v as PostQrCodeAlignVertical,
                      })
                    }
                  />
                </FormGroup>
              </>
            )}
            <FormGroup disabled={disabled}>
              <Switch
                disabled={disabled}
                label="Hide Caption"
                checked={!!post.qrCodeStyle?.textHidden}
                onChange={() =>
                  updateStyle({ textHidden: !post.qrCodeStyle?.textHidden })
                }
              />
            </FormGroup>
          </div>
        </Card>
      </div>
    </div>
  );
}
